import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Popover } from 'antd';
import { Para, Div, ParaHeader, Title, Wrapper } from './styled';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { featureSelector } from '@src/store/common/selectors';
import featureNames from '@src/utils/featureToggle/constants';
import { metricsContainerRefAtom } from '@src/atoms/page';
import { usePopupOnScroll } from '@src/hooks/utils/usePopupOnScroll';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { upperFirst } from 'lodash';

const MetricHelpContent = ({ metricInfo }) => {
  const { helpText, helpLink, definition, helpImg } = metricInfo;

  if (helpImg) {
    return <img src={helpImg} style={{ width: 600 }} />;
  }

  // process help text
  let text = helpText;
  if (Array.isArray(helpText)) {
    text = helpText.map((ht) => <div key={ht}>{ht}</div>);
  }
  // process help links
  const links: any[] = [];
  const { origin } = window.location;
  // Relative links are resolved against the app origin; absolute URLs are used as-is.
  const resolveHref = (link: string) => (/^https?:\/\//.test(link) ? link : `${origin}${link}`);
  if (Array.isArray(helpLink)) {
    helpLink.forEach((hl, idx) => {
      links.push(
        <a href={resolveHref(hl.link)} target="_blank" rel="noreferrer">
          {hl.text}
        </a>,
      );
      if (idx !== helpLink.length - 1) {
        links.push(` and `);
      }
    });
  } else if (typeof helpLink === 'string' && helpLink.trim()) {
    links.push(
      <a href={resolveHref(helpLink)} target="_blank" rel="noreferrer">
        {metricInfo.displayName}
      </a>,
    );
  }

  const activationRedirectionLink = metricInfo?.activationMetricId
    ? `/app/activation/metric-builder/metric/${metricInfo.activationMetricId}`
    : null;
  const renderDefinition = (definition) => {
    const items: { key: string; label: string; desc: string }[] = [];
    const { numerator, denominator, events = [] } = definition ?? {};
    if (numerator || denominator) {
      Object.entries({ numerator, denominator })
        .filter(([_, value]) => value !== undefined)
        .forEach(([key, value]) => {
          items.push({
            key,
            label: upperFirst(key),
            desc: value,
          });
        });
    } else {
      events.forEach((event) => {
        const { pos, name } = event ?? {};
        items.push({
          key: pos + name,
          label: pos,
          desc: name,
        });
      });
    }
    return (
      <ul>
        {items.map((it) => {
          const { key, label, desc } = it;
          return (
            <li key={key}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontWeight: '500', minWidth: '105px' }}>{label}:</span>
                <span style={{ wordBreak: 'break-word' }}>{desc}</span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Wrapper>
      <ParaHeader>
        <Title>{metricInfo.displayName}</Title>
      </ParaHeader>
      {Array.isArray(text) ? (
        <Para>{text}</Para>
      ) : (
        <Div dangerouslySetInnerHTML={{ __html: text }} />
      )}
      {renderDefinition(definition)}
      {links.length !== 0 && (
        <Para>
          {`For more details, see `}
          {links}
          {'.'}
        </Para>
      )}
      {activationRedirectionLink && (
        <Div>
          {`For more details, see `}
          <a href={activationRedirectionLink} target="_blank" rel="noreferrer">
            {` ${metricInfo.displayName}`}
          </a>
        </Div>
      )}
    </Wrapper>
  );
};

export const MetricHelpPopup = ({ metric }) => {
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const [helpOpen, setHelpOpen] = useState(false);
  const metricInfo =
    getMetricMetaByKey({
      metricKey: metric,
      flowOverride: !!flowId,
      frustrationSignalOverride: !!frustrationSignalId,
    }) || {};
  const { helpText, definition, helpImg } = metricInfo;
  const enableMetricHelp = useSelector(featureSelector(featureNames.METRIC_HELP));
  const metricsContainerRef = useAtomValue(metricsContainerRefAtom);

  usePopupOnScroll(
    React.useCallback(() => {
      requestAnimationFrame(() => setHelpOpen(false));
    }, []),
    [metricsContainerRef?.current],
  );

  if (!enableMetricHelp || !(helpImg || helpText || definition)) {
    return null;
  }

  return (
    <div>
      <Popover
        content={<MetricHelpContent metricInfo={metricInfo} />}
        trigger="click"
        open={helpOpen}
        placement="top"
        autoAdjustOverflow={true}
        destroyOnHidden={true}
        onOpenChange={setHelpOpen}
      >
        <Button
          onClick={() => setHelpOpen(true)}
          type="text"
          icon={<InfoCircleOutlined />}
          size="small"
          title="Info"
          disabled={false}
        />
      </Popover>
    </div>
  );
};

export default MetricHelpPopup;
